import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { staffService, type User } from '../../services/staff';

interface ScheduleUserSearchProps {
  selected: User | null;
  onSelect: (user: User | null) => void;
}

// The search API can return users without a username or full name (e.g. SSO
// profiles), so display fields are treated as possibly null despite the type.
function displayName(u: User): string {
  return u.full_name || u.username || u.email || '—';
}

function initial(u: User): string {
  return (displayName(u).trim()[0] || '?').toUpperCase();
}

/** Debounced user search + result list + selected chip, shared by the staff
 * add-user and bulk-register dialogs (mirrors GymStaffManager). */
export function ScheduleUserSearch({ selected, onSelect }: ScheduleUserSearchProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (selected || query.trim().length < 2) {
      setResults([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearching(true);
      staffService
        .searchUsers(query.trim())
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, selected]);

  if (selected) {
    return (
      <div className="flex items-center gap-3 rounded-md border p-2">
        <Avatar className="h-8 w-8">
          {selected.avatar_small_url ? <AvatarImage src={selected.avatar_small_url} alt={displayName(selected)} /> : null}
          <AvatarFallback className="text-xs">{initial(selected)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{displayName(selected)}</div>
          {selected.username && <div className="text-xs text-muted-foreground truncate">@{selected.username}</div>}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => {
            onSelect(null);
            setQuery('');
          }}
          aria-label={t('common.cancel')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t('schedule.bulkRegister.userPlaceholder')}
        />
      </div>
      {searching && <p className="text-xs text-muted-foreground">…</p>}
      {results.length > 0 && (
        <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
          {results.map(u => (
            <button
              key={u.id}
              type="button"
              className="flex w-full items-center gap-3 p-2 text-left hover:bg-muted transition-colors"
              onClick={() => {
                onSelect(u);
                setResults([]);
              }}
            >
              <Avatar className="h-8 w-8">
                {u.avatar_small_url ? <AvatarImage src={u.avatar_small_url} alt={displayName(u)} /> : null}
                <AvatarFallback className="text-xs">{initial(u)}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{displayName(u)}</div>
                {u.username && <div className="text-xs text-muted-foreground truncate">@{u.username}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </>
  );
}
